import { Link } from "react-router";
import { ChevronUp, ChevronDown, type LucideIcon } from "lucide-react";
import { Sparkline } from "./Sparkline";
import { cn } from "@/lib/utils";

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
  /** Brand hex used to tint the icon, delta and sparkline. Defaults to brand blue. */
  color?: string;
  linkTo?: string;
  subtitle?: string;
  /** Trend label, e.g. "+3 / 14 dni", derived from the real stats trend. */
  delta?: string;
  deltaDir?: "up" | "down";
  /** Daily trend series from GET /admin/stats, drives the sparkline. */
  spark?: number[];
}

export function StatCard({
  icon: Icon,
  label,
  value,
  color = "#1F5A9E",
  linkTo,
  subtitle,
  delta,
  deltaDir = "up",
  spark,
}: StatCardProps) {
  const content = (
    <div className="flex h-full flex-col gap-2 rounded-xl border border-black/5 bg-pulso-surface p-5 transition-shadow hover:shadow-md">
      <div className="flex items-center justify-between">
        <div
          className="flex h-9 w-9 items-center justify-center rounded-[10px]"
          style={{ background: `${color}1A`, color }}
        >
          <Icon className="h-[18px] w-[18px]" />
        </div>
        {delta && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold",
              deltaDir === "up"
                ? "bg-pulso-success/10 text-pulso-success"
                : "bg-pulso-orange/10 text-pulso-orange",
            )}
          >
            {deltaDir === "up" ? (
              <ChevronUp className="h-3 w-3" strokeWidth={3} />
            ) : (
              <ChevronDown className="h-3 w-3" strokeWidth={3} />
            )}
            {delta}
          </span>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-pulso-muted">{label}</p>
        <p className="font-brand text-3xl font-bold leading-tight tracking-tight">{value}</p>
      </div>
      {subtitle && <p className="text-xs text-pulso-muted">{subtitle}</p>}
      {spark && <Sparkline points={spark} color={color} />}
    </div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="block">
        {content}
      </Link>
    );
  }
  return content;
}
